import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Globe,
  ShieldCheck,
  Users,
  Car,
  Building,
  Map,
  User,
  Compass,
  Globe2,
  FileText,
  MoreHorizontal,
  ArrowRight,
  ChevronDown,
  Briefcase,
  Plane,
  PlaneTakeoff
} from 'lucide-react';
import './HomePage.css';

// Placeholder examples for animated typing effect
const PLACEHOLDER_EXAMPLES = [
  'Innova in Kerala',
  'Hotels in Goa',
  'Tour Guide in Manali',
  'DMC in Rajasthan',
  'Visa Assistance Delhi',
  'Homestay in Shimla'
];

const CATEGORIES = [
  { id: 'b2b-cab-transport', name: 'B2B Cab & Transport', icon: Car },
  { id: 'b2b-hotel-homestay', name: 'B2B Hotel & Homestay', icon: Building },
  { id: 'tour-operator', name: 'Tour Operator', icon: Map },
  { id: 'local-guide', name: 'Local Guide', icon: User },
  { id: 'activities-experiences', name: 'Activities & Experiences', icon: Compass },
  { id: 'dmc', name: 'DMC', icon: Globe2 },
  { id: 'visa-assistance', name: 'Visa Assistance', icon: FileText },
  { id: 'b2b-group-tour', name: 'B2B Group Tour Service', icon: Users },
  { id: 'b2b-travel-package', name: 'B2B Travel Package', icon: Briefcase },
  { id: 'international-tour-package', name: 'International Tour Package', icon: Plane },
  { id: 'flight-booking', name: 'Flight Booking Services', icon: PlaneTakeoff },
  { id: 'other', name: 'Other', icon: MoreHorizontal }
];

const FEATURES = [
  {
    icon: Globe,
    title: 'Pan-India Network',
    description: 'Access B2B travel service providers from Kashmir to Kanyakumari.',
    color: 'blue'
  },
  {
    icon: ShieldCheck,
    title: 'Trusted & Verified Providers',
    description: 'Trusted partners verified by mobile authentication.',
    color: 'teal'
  },
  {
    icon: Users,
    title: 'Direct Connections',
    description: 'Connect directly with travel vendors. No commission, no hidden fees.',
    color: 'amber'
  }
];

const HomePage = ({ onPostService }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');

  // Animated placeholder typing effect
  useEffect(() => {
    let currentIndex = 0;
    let currentText = '';
    let isDeleting = false;
    let timeoutId;

    const typeText = () => {
      const fullText = PLACEHOLDER_EXAMPLES[currentIndex];

      if (!isDeleting) {
        // Typing
        currentText = fullText.substring(0, currentText.length + 1);
        setAnimatedPlaceholder(currentText);

        if (currentText === fullText) {
          // Pause before deleting
          timeoutId = setTimeout(() => {
            isDeleting = true;
            typeText();
          }, 2000);
          return;
        }
      } else {
        // Deleting
        currentText = fullText.substring(0, currentText.length - 1);
        setAnimatedPlaceholder(currentText);

        if (currentText === '') {
          isDeleting = false;
          currentIndex = (currentIndex + 1) % PLACEHOLDER_EXAMPLES.length;
        }
      }

      const typingSpeed = isDeleting ? 50 : 100;
      timeoutId = setTimeout(typeText, typingSpeed);
    };

    typeText();

    return () => clearTimeout(timeoutId);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigateToBrowse(searchQuery);
  };

  const navigateToBrowse = (query) => {
    const params = new URLSearchParams();
    if (query) params.set('search', query);
    if (selectedCategory && selectedCategory !== 'All Categories') {
      params.set('category', selectedCategory);
    }
    navigate(`/browse?${params.toString()}`);
  }

  const handleCategoryClick = (categoryName) => {
    navigate(`/browse?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="container hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="hero-title-line">One Platform for All</span>
              <span className="hero-title-accent">B2B Travel Services</span>
            </h1>
            <p className="hero-subtitle">
              Connect directly with verified tour operators, cab providers, hotels, and
              local guides across India. No middlemen, just business.
            </p>

            {/* Search Box */}
            <form className="hero-search" onSubmit={handleSearch}>
              <div className="search-box">
                <div className="search-input-wrapper">
                  <Search className="search-icon" size={20} />
                  <input
                    type="text"
                    placeholder={`Search services (e.g. ${animatedPlaceholder})`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="search-divider"></div>
                <div className="search-select-wrapper">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="search-select"
                  >
                    <option>All Categories</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="select-icon" size={18} />
                </div>
                <button type="submit" className="search-btn">
                  Search
                </button>
              </div>
            </form>

            <div className="hero-actions">
              <button
                className="browse-all-btn"
                onClick={() => navigate('/browse')}
              >
                <Compass size={20} />
                Browse All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="features-grid">
            {FEATURES.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className={`feature-icon feature-icon-${feature.color}`}>
                  <feature.icon size={28} strokeWidth={1.5} />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories" id="categories">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Explore Categories</h2>
              <p className="section-subtitle">Find exactly what your clients need</p>
            </div>
            <button
              className="view-all-btn"
              onClick={() => navigate('/browse')}
            >
              View All
              <ArrowRight size={18} />
            </button>
          </div>
          <div className="categories-grid">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                className="category-card"
                onClick={() => handleCategoryClick(category.name)}
              >
                <category.icon className="category-icon" size={24} strokeWidth={1.5} />
                <span className="category-name">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container cta-container">
          <h2 className="cta-title">Are you a Travel Service Provider?</h2>
          <p className="cta-description">
            Join thousands of travel businesses growing their network. List your services for
            free and get leads directly from agents.
          </p>
          <button className="btn btn-accent btn-lg" onClick={onPostService}>
            List Your Service Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

